import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { feedbackApi } from '../../services/api';
import { toast } from 'react-toastify';
import { HandThumbUpIcon, HandThumbDownIcon } from '@heroicons/react/24/outline';
import { HandThumbUpIcon as HandThumbUpSolidIcon, HandThumbDownIcon as HandThumbDownSolidIcon } from '@heroicons/react/24/solid';

const RatingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.$compact ? '4px' : '12px'};
  ${props => !props.$compact && 'margin: 16px 0;'}
`;

const RatingLabel = styled.span`
  font-size: 0.8125rem;
  color: #5F6368;
  font-weight: 500;
`;

const RatingButtons = styled.div`
  display: flex;
  gap: ${props => props.$compact ? '2px' : '8px'};
`;

const RatingBtn = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.$compact ? '0' : '6px'};
  padding: ${props => props.$compact ? '4px' : '8px 16px'};
  border-radius: ${props => props.$compact ? '6px' : '8px'};
  border: ${props => props.$compact
    ? (props.selected ? `1.5px solid ${props.color}` : '1px solid #E8EAED')
    : `2px solid ${props.selected ? props.color : '#E8EAED'}`
  };
  background: ${props => props.selected ? (props.$compact ? `${props.color}15` : props.color) : 'transparent'};
  color: ${props => props.selected ? (props.$compact ? props.color : 'white') : '#5F6368'};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.8125rem;
  font-weight: 500;

  &:hover:not(:disabled) {
    border-color: ${props => props.color};
    background: ${props => props.selected ? (props.$compact ? `${props.color}20` : props.color) : `${props.color}10`};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: ${props => props.$compact ? '16px' : '20px'};
    height: ${props => props.$compact ? '16px' : '20px'};
  }
`;

/**
 * Rating Button Component
 * Allows users to rate itineraries with thumbs up/down
 * compact: icon-only mode for inline use
 */
export const RatingButton = ({ itineraryId, userId, compact }) => {
  const [rating, setRating] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadRatingStats = useCallback(async () => {
    try {
      const response = await feedbackApi.getItineraryRating(itineraryId, userId);
      setRating(response.data.userRating || null);
    } catch (error) {
      console.error('Failed to load rating stats:', error);
    }
  }, [itineraryId, userId]);

  useEffect(() => {
    if (itineraryId && userId) {
      loadRatingStats();
    }
  }, [itineraryId, userId, loadRatingStats]);

  const handleRate = async (newRating) => {
    if (loading) return;

    setLoading(true);
    try {
      await feedbackApi.rateItinerary(itineraryId, userId, newRating);
      setRating(newRating);

      if (newRating === 1) {
        toast.success('Thanks for the positive feedback!');
      } else {
        toast.success('Thanks for your feedback.');
      }

      await loadRatingStats();
    } catch (error) {
      console.error('Failed to submit rating:', error);
      toast.error('Failed to submit rating. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!userId) {
    return null;
  }

  return (
    <RatingContainer $compact={compact}>
      {!compact && <RatingLabel>Rate this itinerary:</RatingLabel>}

      <RatingButtons $compact={compact}>
        <RatingBtn
          onClick={() => handleRate(1)}
          disabled={loading}
          selected={rating === 1}
          color="#34A853"
          $compact={compact}
          whileTap={{ scale: 0.9 }}
          title="Helpful"
        >
          {rating === 1 ? <HandThumbUpSolidIcon /> : <HandThumbUpIcon />}
          {!compact && 'Helpful'}
        </RatingBtn>

        <RatingBtn
          onClick={() => handleRate(-1)}
          disabled={loading}
          selected={rating === -1}
          color="#EA4335"
          $compact={compact}
          whileTap={{ scale: 0.9 }}
          title="Not helpful"
        >
          {rating === -1 ? <HandThumbDownSolidIcon /> : <HandThumbDownIcon />}
          {!compact && 'Not helpful'}
        </RatingBtn>
      </RatingButtons>
    </RatingContainer>
  );
};

export default RatingButton;
